import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData, postData, updateData, deleteData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import {
  Add,
  Edit,
  Visibility,
  Delete,
  People,
  LocalShipping,
  Warning,
  Search,
  FilterList,
  Refresh
} from '@mui/icons-material';
import {
  Button,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box
} from '@mui/material';
import MasterModal from './MasterModal';
import './MasterDashboard.css';

const MasterDashboard = () => {
  // Session validation
  useSessionValidation();
  
  const { companyId, token, userId } = useAuth();
  const { logAudit } = useAuditLog();
  const [activeTab, setActiveTab] = useState('overview');

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'client' or 'vendor'
  const [modalAction, setModalAction] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Data states
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [invoiceTypes, setInvoiceTypes] = useState([]);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form states
  const [formData, setFormData] = useState({});

  // Separate data states for Master, LedgerGroup, Ledger
  const [masterData, setMasterData] = useState({});
  const [ledgerGroupData, setLedgerGroupData] = useState({});
  const [ledgerData, setLedgerData] = useState({});

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [loadingVariant, setLoadingVariant] = useState('default');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete confirmation modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemName, setDeleteItemName] = useState('');
  const [deleteItemType, setDeleteItemType] = useState('');

  useEffect(() => {
    if (companyId && token) {
      fetchAllData();
    }
  }, [companyId, token]);

  const fetchClients = async () => {
    try {
      const response = await fetchData('ClientAndVendorApi', `/ClientList/${companyId}`, token);
      setClients(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await fetchData('ClientAndVendorApi', `/VendorList/${companyId}`, token);
      setVendors(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      setVendors([]);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    setLoadingMessage('Fetching data...');
    await Promise.allSettled([fetchClients(), fetchVendors(), fetchInvoiceTypes()]);
    setLoading(false);
  };

  const fetchInvoiceTypes = async () => {
    try {
      const response = await fetchData('InvoiceTypeApi', `/${companyId}`, token);
      setInvoiceTypes(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching invoice types:', error);
      setInvoiceTypes([]);
    }
  };

  const fetchSingleItem = async (id) => {
    try {
      const response = await fetchData('ClientAndVendorApi', `/ClientAndVendorDetails/${id}`, token);
      console.log('API Response:', response); // Debug log
      
      // Handle different response formats
      let data = null;
      if (response.response_data) {
        data = response.response_data;
      } else if (response.Master || response.Ledger || response.LedgerGroup) {
        data = response;
      } else if (response.responseCode === '1111' && response.response_data) {
        data = response.response_data;
      }
      
      if (data) {
        return {
          master: data.Master || data.master || {},
          ledgerGroup: data.LedgerGroup || data.ledgerGroup || {},
          ledger: data.Ledger || data.ledger || {}
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching Client/Vendor details:', error);
      return null;
    }
  };

  const handleOpenModal = async (type, action, itemOrId = null) => {
    setModalType(type);
    setModalAction(action);

    // Get the ID - could be an object with Id property or just the ID number
    const itemId = typeof itemOrId === 'object' ? itemOrId?.Id : itemOrId;
    console.log('Opening modal for ID:', itemId, 'Action:', action); // Debug log
    
    if ((action === 'view' || action === 'edit') && itemId) {
      setLoading(true);
      setLoadingMessage(`Fetching ${type} details...`);
      
      const freshData = await fetchSingleItem(itemId);
      console.log('Fetched data:', freshData); // Debug log
      
      if (freshData && (freshData.master?.Id || freshData.ledger?.Id)) {
        setMasterData(freshData.master);
        setLedgerGroupData(freshData.ledgerGroup);
        setLedgerData(freshData.ledger);
        
        // Combine all data into formData for the modal
        const combinedData = {
          // Master fields
          Id: freshData.master.Id,
          MasterId: freshData.master.Id,
          Name: freshData.master.Name || '',
          SearchableName: freshData.master.SearchableName || '',
          BalanceSheetHeadId: freshData.master.BalanceSheetHeadId,
          BalanceSheetHeadName: freshData.master.BalanceSheetHeadName || '',
          OpeningBalance: freshData.master.OpeningBalance ?? 0,
          OpeningDate: freshData.master.OpeningDate,
          IsPayableOrReceivable: freshData.master.IsPayableOrReceivable,
          IsActive: freshData.master.IsActive ?? true,
          // LedgerGroup fields
          LedgerGroupId: freshData.ledgerGroup.Id,
          LedgerGroupName: freshData.ledgerGroup.Name || '',
          GroupCreditDays: freshData.ledgerGroup.CreditDays ?? 0,
          GroupCreditLimit: freshData.ledgerGroup.CreditLimit ?? 0,
          // Ledger fields
          LedgerId: freshData.ledger.Id,
          IsType: freshData.ledger.IsType,
          InvoiceTypeId: freshData.ledger.InvoiceTypeId ?? 0,
          SalesExecutiveId: freshData.ledger.SalesExecutiveId ?? 0,
          StateCodeId: freshData.ledger.StateCodeId,
          AddressLine1: freshData.ledger.AddressLine1 || '',
          AddressLine2: freshData.ledger.AddressLine2 || '',
          Location: freshData.ledger.Location || '',
          PinCode: freshData.ledger.PinCode || '',
          DeliveryAddressLine1: freshData.ledger.DeliveryAddressLine1 || '',
          DeliveryAddressLine2: freshData.ledger.DeliveryAddressLine2 || '',
          DeliveryLocation: freshData.ledger.DeliveryLocation || '',
          DeliveryPinCode: freshData.ledger.DeliveryPinCode || '',
          ContactNumber1: freshData.ledger.ContactNumber1 || '',
          ContactNumber2: freshData.ledger.ContactNumber2 || '',
          ContactNumber3: freshData.ledger.ContactNumber3 || '',
          Email: freshData.ledger.Email || '',
          GSTNumber: freshData.ledger.GSTNumber || '',
          PanNumber: freshData.ledger.PanNumber || '',
          MSMENumber: freshData.ledger.MSMENumber || '',
          CreditDays: freshData.ledger.CreditDays ?? 0,
          CreditLimit: freshData.ledger.CreditLimit ?? 0
        };
        
        console.log('Combined formData:', combinedData); // Debug log
        setSelectedItem(combinedData);
        setFormData(combinedData);
      } else {
        // Fallback if fetch fails - use the item data from the list
        console.log('Fetch failed, using fallback'); // Debug log
        const item = typeof itemOrId === 'object' ? itemOrId : null;
        setSelectedItem(item);
        setFormData(item || {});
      }
      
      setLoading(false);
    } else {
      // Create mode - reset all data
      setMasterData({});
      setLedgerGroupData({});
      setLedgerData({});
      setSelectedItem(null);
      setFormData({});
    }

    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalType('');
    setModalAction('');
    setSelectedItem(null);
    setFormData({});
    setMasterData({});
    setLedgerGroupData({});
    setLedgerData({});
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      const entityName = modalType.charAt(0).toUpperCase() + modalType.slice(1);
      const actionText = modalAction === 'create' ? 'Creating' : 'Updating';
      setLoadingMessage(`${actionText} ${entityName.toLowerCase()}...`);
      setLoadingVariant('default');

      // Get the Master ID for edit mode
      const masterId = formData.Id || formData.MasterId || 0;
      const ledgerGroupId = formData.LedgerGroupId || 0;
      const ledgerId = formData.LedgerId || 0;

      // Structure data as ClientAndVendorVM pattern for API
      const dataToSend = {
        Master: {
          Id: modalAction === 'edit' ? masterId : 0,
          CompanyId: companyId,
          BalanceSheetHeadId: formData.BalanceSheetHeadId || 0,
          MasterTypeId: formData.MasterTypeId || 1,
          Name: formData.Name || '',
          SearchableName: formData.SearchableName || formData.Name || '',
          OpeningBalance: parseFloat(formData.OpeningBalance) || 0,
          OpeningDate: formData.OpeningDate ? formData.OpeningDate.split('T')[0] : new Date().toISOString().split('T')[0],
          IsPayableOrReceivable: formData.IsPayableOrReceivable ?? (modalType === 'client'),
          IsActive: formData.IsActive ?? true,
          CreatedBy: modalAction === 'create' ? (userId || 0) : (masterData.CreatedBy || userId || 0),
          ModifiedBy: modalAction === 'edit' ? (userId || 0) : null,
          CreatedDate: modalAction === 'edit' ? masterData.CreatedDate : null,
          ModifiedDate: modalAction === 'edit' ? new Date().toISOString() : null
        },
        LedgerGroup: {
          Id: modalAction === 'edit' ? ledgerGroupId : 0,
          CompanyId: companyId,
          MasterId: modalAction === 'edit' ? masterId : 0,
          Name: formData.LedgerGroupName || '',
          CreditDays: parseInt(formData.GroupCreditDays) || 0,
          CreditLimit: parseFloat(formData.GroupCreditLimit) || 0,
          IsActive: true,
          CreatedBy: modalAction === 'create' ? (userId || 0) : (ledgerGroupData.CreatedBy || userId || 0),
          ModifiedBy: modalAction === 'edit' ? (userId || 0) : null,
          CreatedDate: modalAction === 'edit' ? ledgerGroupData.CreatedDate : null,
          ModifiedDate: modalAction === 'edit' ? new Date().toISOString() : null
        },
        Ledger: {
          Id: modalAction === 'edit' ? ledgerId : 0,
          CompanyId: companyId,
          MasterId: modalAction === 'edit' ? masterId : 0,
          LedgerGroupId: modalAction === 'edit' ? ledgerGroupId : 0,
          IsType: formData.IsType || (modalType === 'client' ? 1 : 2),
          InvoiceTypeId: formData.InvoiceTypeId || 0,
          SalesExecutiveId: formData.SalesExecutiveId || 0,
          StateCodeId: formData.StateCodeId || null,
          // Branch Address Details
          AddressLine1: formData.AddressLine1 || '',
          AddressLine2: formData.AddressLine2 || '',
          Location: formData.Location || '',
          PinCode: formData.PinCode || '',
          // Delivery Address Details
          DeliveryAddressLine1: formData.DeliveryAddressLine1 || '',
          DeliveryAddressLine2: formData.DeliveryAddressLine2 || '',
          DeliveryLocation: formData.DeliveryLocation || '',
          DeliveryPinCode: formData.DeliveryPinCode || '',
          // Contact Details
          ContactNumber1: formData.ContactNumber1 || '',
          ContactNumber2: formData.ContactNumber2 || '',
          ContactNumber3: formData.ContactNumber3 || '',
          Email: formData.Email || '',
          // Other Details
          GSTNumber: formData.GSTNumber || '',
          PanNumber: formData.PanNumber || '',
          MSMENumber: formData.MSMENumber || '',
          CreditDays: parseInt(formData.CreditDays) || 0,
          CreditLimit: parseFloat(formData.CreditLimit) || 0,
          OpeningBalance: parseFloat(formData.OpeningBalance) || 0,
          OpeningDate: formData.OpeningDate ? formData.OpeningDate.split('T')[0] : new Date().toISOString().split('T')[0],
          IsPayableOrReceivable: formData.IsPayableOrReceivable ?? (modalType === 'client'),
          IsActive: formData.IsActive ?? true,
          CreatedBy: modalAction === 'create' ? (userId || 0) : (ledgerData.CreatedBy || userId || 0),
          ModifiedBy: modalAction === 'edit' ? (userId || 0) : null,
          CreatedDate: modalAction === 'edit' ? ledgerData.CreatedDate : null,
          ModifiedDate: modalAction === 'edit' ? new Date().toISOString() : null
        }
      };

      console.log('Data to send:', dataToSend); // Debug log

      if (modalAction === 'create') {
        await postData('ClientAndVendorApi', '', dataToSend, token);
        setSuccess(`${entityName} created successfully!`);
      } else if (modalAction === 'edit') {
        if (!masterId) {
          throw new Error('Master ID is missing');
        }
        await updateData('ClientAndVendorApi', masterId, dataToSend, token);
        setSuccess(`${entityName} updated successfully!`);
      }

      handleCloseModal();

      // Refresh the specific data type
      if (modalType === 'client') {
        await fetchClients();
      } else {
        await fetchVendors();
      }

      setLoading(false);

    } catch (error) {
      console.error('Error saving:', error);
      setError(`Failed to ${modalAction} ${modalType}. Please try again.`);
      setLoading(false);
    }
  };

  const handleOpenDeleteModal = (type, id, name) => {
    setDeleteItemType(type);
    setDeleteItemId(id);
    setDeleteItemName(name);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteItemId(null);
    setDeleteItemName('');
    setDeleteItemType('');
  };

  const handleDelete = async () => {
    if (!deleteItemId || !deleteItemType) return;
    
    setLoading(true);
    handleCloseDeleteModal();
    try {
      await deleteData('ClientAndVendorApi', deleteItemId, token);
      setSuccess(`${deleteItemType.charAt(0).toUpperCase() + deleteItemType.slice(1)} deleted successfully!`);
      if (deleteItemType === 'client') {
        await fetchClients();
      } else {
        await fetchVendors();
      }
    } catch (error) {
      console.error('Error deleting:', error);
      setError(`Failed to delete ${deleteItemType}.`);
    }
    setLoading(false);
  };

  const getFilteredData = (data) => {
    let filtered = data;

    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.Code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.ContactNumber1?.includes(searchTerm) ||
        item.Email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterActive !== 'all') {
      filtered = filtered.filter(item =>
        filterActive === 'active' ? item.IsActive : !item.IsActive
      );
    }

    return filtered;
  };

  const getPaginatedData = (data) => {
    const filtered = getFilteredData(data);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return {
      data: filtered.slice(startIndex, endIndex),
      totalCount: filtered.length,
      totalPages: Math.ceil(filtered.length / rowsPerPage),
      startIndex
    };
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    fetchAllData();
  };

  const handleCloseSnackbar = () => {
    setError('');
    setSuccess('');
  };

  // Stats calculations
  const activeClients = clients.filter(c => c.IsActive).length;
  const activeVendors = vendors.filter(v => v.IsActive).length;
  const dormantClients = clients.filter(c => !c.IsActive);
  const dormantVendors = vendors.filter(v => !v.IsActive);

  const renderOverview = () => (
    <div className="overview-section">
      <div className="stats-cards">
        <div className="stat-card" onClick={() => setActiveTab('clients')}>
          <div className="stat-icon blue-icon">
            <People />
          </div>
          <div className="stat-content">
            <span className="stat-label">Total Clients</span>
            <span className="stat-number">{clients.length}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green-icon">
            <People />
          </div>
          <div className="stat-content">
            <span className="stat-label">Active Clients</span>
            <span className="stat-number">{activeClients}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon orange-icon">
            <Warning />
          </div>
          <div className="stat-content">
            <span className="stat-label">Dormant Clients</span>
            <span className="stat-number">{dormantClients.length}</span>
          </div>
        </div>

        <div className="stat-card" onClick={() => setActiveTab('vendors')}>
          <div className="stat-icon purple-icon">
            <LocalShipping />
          </div>
          <div className="stat-content">
            <span className="stat-label">Total Vendors</span>
            <span className="stat-number">{vendors.length}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon teal-icon">
            <LocalShipping />
          </div>
          <div className="stat-content">
            <span className="stat-label">Active Vendors</span>
            <span className="stat-number">{activeVendors}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon red-icon">
            <Warning />
          </div>
          <div className="stat-content">
            <span className="stat-label">Dormant Vendors</span>
            <span className="stat-number">{dormantVendors.length}</span>
          </div>
        </div>
      </div>

      <div className="management-row">
        <div className="management-card">
          <div className="management-card-content">
            <h3>Manage Clients</h3>
            <p>Create and manage client information.</p>
          </div>
          <Button variant="contained" className="view-button" onClick={() => setActiveTab('clients')}>
            View Clients
          </Button>
        </div>

        <div className="management-card">
          <div className="management-card-content">
            <h3>Manage Vendors</h3>
            <p>Create and manage vendor information.</p>
          </div>
          <Button variant="contained" className="view-button" onClick={() => setActiveTab('vendors')}>
            View Vendors
          </Button>
        </div>
      </div>
    </div>
  );

  const renderDataTable = (data, type) => {
    const { data: paginatedData, totalCount, totalPages, startIndex } = getPaginatedData(data);

    return (
      <div className="data-table-container">
        <div className="table-header">
          <div className="search-filter-section">
            <div className="search-box">
              <Search className="search-icon" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              />
            </div>
            <div className="filter-section">
              <FilterList className="filter-icon" />
              <select value={filterActive} onChange={(e) => { setFilterActive(e.target.value); setCurrentPage(1); }}>
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            {/* Desktop buttons with text */}
            <Button
              variant="outlined"
              onClick={handleRefresh}
              disabled={loading}
              className="refresh-button desktop-only"
              startIcon={<Refresh />}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenModal(type, 'create')}
              className="add-button desktop-only"
            >
              Add {type.charAt(0).toUpperCase() + type.slice(1)}
            </Button>
            {/* Mobile icon buttons */}
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} disabled={loading} className="refresh-icon-btn mobile-only">
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title={`Add ${type.charAt(0).toUpperCase() + type.slice(1)}`}>
              <IconButton onClick={() => handleOpenModal(type, 'create')} className="add-icon-btn mobile-only">
                <Add />
              </IconButton>
            </Tooltip>
          </div>
        </div>

        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Sl No.</th>
                <th>Name</th>
                <th>Ledger Group</th>
                <th>Contact</th>
                <th>GST Number</th>
                <th>Pin Code</th>
                <th>Type</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, index) => (
                <tr key={item.Id}>
                  <td>
                    <span
                      onClick={() => handleOpenModal(type, 'edit', item)}
                      style={{ color: '#1e40af', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline' }}
                    >
                      {startIndex + index + 1}
                    </span>
                  </td>
                  <td>{item.Name}</td>
                  <td>{item.LedgerGroupName || '-'}</td>
                  <td>{item.ContactNumber1 || '-'}</td>
                  <td>{item.GSTNumber || '-'}</td>
                  <td>{item.PinCode || '-'}</td>
                  <td>
                    <Chip
                      label={item.IsType === 1 ? 'Client' : item.IsType === 2 ? 'Supplier' : item.IsType === 3 ? 'Both' : '-'}
                      color={item.IsType === 3 ? 'primary' : item.IsType === 1 ? 'info' : item.IsType === 2 ? 'secondary' : 'default'}
                      size="small"
                      sx={{ fontSize: '0.65rem', height: '20px' }}
                    />
                  </td>
                  <td>
                    <Chip
                      label={item.IsActive ? 'Active' : 'Inactive'}
                      color={item.IsActive ? 'success' : 'default'}
                      size="small"
                      sx={{ fontSize: '0.65rem', height: '20px' }}
                    />
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="action-buttons">
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => handleOpenModal(type, 'view', item.MasterId)}>
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpenModal(type, 'edit', item.MasterId)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleOpenDeleteModal(type, item.MasterId, item.Name)}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Section */}
        <div className="pagination-section">
          <div className="pagination-info">
            <span>Showing {startIndex + 1} to {Math.min(startIndex + rowsPerPage, totalCount)} of {totalCount} entries</span>
            <div className="rows-per-page">
              <span>Rows per page:</span>
              <select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            >
              First
            </button>
            <button
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Prev
            </button>
            {[...Array(totalPages)].map((_, i) => {
              const page = i + 1;
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <button
                    key={page}
                    className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                );
              } else if (page === currentPage - 2 || page === currentPage + 2) {
                return <span key={page} className="pagination-ellipsis">...</span>;
              }
              return null;
            })}
            <button
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
            <button
              className="pagination-btn"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'clients':
        return renderDataTable(clients, 'client');
      case 'vendors':
        return renderDataTable(vendors, 'vendor');
      default:
        return renderOverview();
    }
  };

  return (
    <div className="master-dashboard">
      <div className="dashboard-header">
        <h1 className="plain-header">Master Management</h1>
      </div>

      {activeTab !== 'overview' && (
        <div className="dashboard-tabs">
          <div className="tab-buttons">
            <button className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
              Overview
            </button>
            <button className={`tab-button ${activeTab === 'clients' ? 'active' : ''}`} onClick={() => setActiveTab('clients')}>
              Clients
            </button>
            <button className={`tab-button ${activeTab === 'vendors' ? 'active' : ''}`} onClick={() => setActiveTab('vendors')}>
              Vendors
            </button>
          </div>
        </div>
      )}

      <div className="dashboard-content">
        {renderTabContent()}
      </div>

      <LogoLoader open={loading} message={loadingMessage} variant={loadingVariant} />

      <MasterModal
        open={openModal}
        onClose={handleCloseModal}
        modalType={modalType}
        modalAction={modalAction}
        selectedItem={selectedItem}
        formData={formData}
        setFormData={setFormData}
        masterData={masterData}
        ledgerGroupData={ledgerGroupData}
        ledgerData={ledgerData}
        onSave={handleSave}
        loading={loading}
        invoiceTypes={invoiceTypes}
      />

      <Snackbar open={!!success} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity="success">{success}</Alert>
      </Snackbar>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity="error">{error}</Alert>
      </Snackbar>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '8px'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              backgroundColor: '#fef2f2',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Warning sx={{ color: '#dc2626', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
              Delete {deleteItemType.charAt(0).toUpperCase() + deleteItemType.slice(1)}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            Are you sure you want to delete <strong style={{ color: '#1e293b' }}>{deleteItemName}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleCloseDeleteModal}
            variant="outlined"
            sx={{
              borderColor: '#e2e8f0',
              color: '#64748b',
              '&:hover': { borderColor: '#cbd5e1', backgroundColor: '#f8fafc' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            sx={{
              backgroundColor: '#dc2626',
              '&:hover': { backgroundColor: '#b91c1c' }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MasterDashboard;


